import type { ChatScroller } from "@ext/agent/components/hooks/useChatScroller";
import { clearMessageJump, getPendingJump, useChatPendingJump } from "@ext/agent/components/store/ChatStore";
import type { ChatMessageJump } from "@ext/agent/components/types/chat";
import { findJumpTarget, flashJumpTarget, getCenteredScrollTop } from "@ext/agent/components/utils/chatMessageJump";
import { getChatScrollPosition } from "@ext/agent/components/utils/chatScrollMemory";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

export const useSessionScrollPosition = (
	scroller: ChatScroller,
	sessionId: string | null,
	hasUserMessage: boolean,
	isOpen: boolean,
) => {
	const placedSessionIdRef = useRef<string | null>(null);
	const pendingJump = useChatPendingJump();

	const jumpToMessage = useCallback(
		(jump: ChatMessageJump, behavior: ScrollBehavior): boolean => {
			clearMessageJump();
			const root = scroller.rootRef.current;
			if (!root) return false;

			const target = findJumpTarget(root, jump);
			if (!target) return false;

			scroller.scrollTo(getCenteredScrollTop(root, target), behavior);
			flashJumpTarget(target);
			return true;
		},
		[scroller],
	);

	const restorePosition = useCallback(
		(id: string | null) => {
			const jump = getPendingJump();
			if (id && jump?.sessionId === id && jumpToMessage(jump, "instant")) return;

			const saved = id ? getChatScrollPosition(id) : undefined;
			if (saved && !saved.atBottom) scroller.scrollTo(saved.top, "instant");
			else scroller.scrollToEnd("instant");
		},
		[jumpToMessage, scroller],
	);

	useLayoutEffect(() => {
		if (!hasUserMessage) {
			placedSessionIdRef.current = null;
			return;
		}
		if (placedSessionIdRef.current === sessionId) return;
		placedSessionIdRef.current = sessionId;
		restorePosition(sessionId);
	}, [sessionId, hasUserMessage, restorePosition]);

	useLayoutEffect(() => {
		if (!isOpen) return;
		restorePosition(placedSessionIdRef.current);
	}, [isOpen, restorePosition]);

	useEffect(() => {
		if (!pendingJump || pendingJump.sessionId !== placedSessionIdRef.current) return;
		jumpToMessage(pendingJump, "smooth");
	}, [pendingJump, jumpToMessage]);
};
